import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../../../contexts/ThemeContext'
import { useTenant } from '../../../contexts/TenantContext'
import { t } from '../../../i18n'

// Helpers every storefront template shares, so each template file can spend
// its lines on layout instead of re-implementing the same behavior.

// The store's own currency (branch setting first - it's what the public
// storefront actually belongs to).
export function useCurrency() {
  const tenant = useTenant()
  const { getCurrency } = useTheme()
  return tenant.settings?.currency || getCurrency()
}

export const price = (value, currency) => `${Number(value || 0).toFixed(2)} ${currency}`

// "45" rather than "45.00" for menus that print whole prices - but never
// round a real 12.50 up to 13.
export const shortPrice = value => { const n = Number(value || 0); return Number.isInteger(n) ? String(n) : n.toFixed(2) }

// Text/image the owner set in the Website editor, with per-template defaults.
export function useStoreContent({ storeName, title, subtitle } = {}) {
  const tenant = useTenant()
  const theme = tenant.settings?.custom_theme?.active === false ? null : tenant.settings?.custom_theme
  return {
    name: storeName || tenant.name || t('Our store'),
    heroTitle: theme?.heroTitle || (title ? t(title) : ''),
    heroSubtitle: theme?.heroSubtitle || tenant.description || (subtitle ? t(subtitle) : ''),
    heroImage: theme?.heroImage || null,
    logoUrl: theme?.logoUrl || tenant.logoUrl || null,
  }
}

// Flattened products with their category, plus search / category / sort
// state - used by the ecommerce templates.
export function useCatalog(menu = []) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [sort, setSort] = useState('featured')
  const products = useMemo(
    () => menu.flatMap(c => (c.items || []).map(p => ({ ...p, categoryName: c.name, categoryId: String(c.id) }))),
    [menu]
  )
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const result = products.filter(p =>
      (category === 'all' || p.categoryId === category) &&
      (!q || `${p.name} ${p.description || ''}`.toLowerCase().includes(q))
    )
    if (sort === 'low') result.sort((a, b) => Number(a.price) - Number(b.price))
    if (sort === 'high') result.sort((a, b) => Number(b.price) - Number(a.price))
    if (sort === 'name') result.sort((a, b) => a.name.localeCompare(b.name))
    return result
  }, [products, category, search, sort])
  const categories = useMemo(() => menu.filter(c => c.items?.length), [menu])
  return { products, filtered, categories, search, setSearch, category, setCategory, sort, setSort }
}

export const SORT_OPTIONS = [
  ['featured', 'Featured'],
  ['low', 'Price: low to high'],
  ['high', 'Price: high to low'],
  ['name', 'Name: A to Z'],
]

// Ecommerce products open their own page; keeps ?branch= in dev.
export function useProductLink() {
  const navigate = useNavigate()
  const location = useLocation()
  return item => navigate(`/product/${item.id}${location.search}`)
}

// The look shared by every page around the template (header, footer, cart,
// product page, checkout, account) so they match the template the owner
// picked. Styles live in index.css under .customer-site[data-skin=...].
const RESTAURANT_SKINS = {
  modern: 'slate', elegant: 'editorial', minimal: 'editorial', luxury: 'noir', steakhouse: 'noir',
  bar: 'neon', sushi: 'washi', cafe: 'cafe', healthy: 'fresh',
}
const STORE_SKINS = { minimal: 'editorial', bold: 'street', vibrant: 'playful' }
const BOOKING_SKINS = { calm: 'spa', professional: 'clinic' }

export function skinFor(tenant) {
  const s = tenant?.settings || {}
  if (tenant?.businessType === 'ecommerce') return STORE_SKINS[s.store_template] || 'classic'
  if (['appointments', 'hotel', 'office'].includes(tenant?.businessType)) return BOOKING_SKINS[s.booking_template] || 'classic'
  return RESTAURANT_SKINS[s.menu_template] || 'classic'
}

// Hidden by the owner, or sold out (stock ran out) - either way it can't be added.
export const isAvailable = item => !(item.is_available === false || item.is_available === 0 || item.in_stock === false)
export const productImage = item => item.images?.[0] || item.image || null

// Text color that stays readable on a background color (#rrggbb).
export function readableOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return '#ffffff'
  const n = parseInt(m[1], 16)
  const [r, g, b] = [n >> 16, (n >> 8) & 255, n & 255].map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4 })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? '#111111' : '#ffffff'
}

// The store's own brand colors (Settings > Theme), when it switched them on.
// They recolor the buttons, links and highlights of whichever template it uses.
export function brandStyle(settings = {}) {
  const valid = c => /^#[0-9a-f]{6}$/i.test(c || '')
  if (!settings.brand_colors_enabled || !valid(settings.primary_color)) return {}
  const primary = settings.primary_color
  const accent = valid(settings.accent_color) ? settings.accent_color : primary
  return {
    '--theme-primary': primary,
    '--theme-accent': accent,
    '--brand': primary,
    '--brand-accent': accent,
    '--sk-on-primary': readableOn(primary),
    '--brand-on': readableOn(primary),
  }
}
